/* agent-store.js — reactive state for the agent dock.
 *
 * The single source of truth for agent-facing UI: autonomy/trust state (now),
 * and the live run timeline (next dock increment). Surfaces bind() to these
 * atoms instead of being poked imperatively, so the composer chip, dock banner,
 * and transcript all reflect one state.
 *
 * Pure view-model helpers (describeAutonomy) are unit-tested; the atoms +
 * refreshAutonomy() are exercised by the dock E2E.
 */

import { signal } from "./store.js";
import { api } from "./api.js";
import { getAuthMode } from "./auth.js";

// Resolved autonomy/status of the active tenant's agent.
//   { status: 'loading' | 'ok' | 'error', tenant?, autonomy?, autonomy_source?,
//     allow_push_tools?, sanitise_on_push?, tenant_url?, tenant_host? }
export const autonomyAtom = signal({ status: "loading" });

/**
 * Apply an /api/agent/status-shaped payload directly into autonomyAtom.
 * Shared by refreshAutonomy()'s own GET below AND the composer autonomy
 * toggle's POST /api/agent/autonomy response (main.js) -- that route
 * deliberately returns the same shape specifically so the caller can apply it
 * here directly instead of firing a redundant follow-up GET (which would also
 * be racy: a slow GET landing after the POST could clobber the just-applied
 * new state back to the pre-toggle value).
 */
export function applyAutonomyStatus(payload) {
  autonomyAtom.set({ status: "ok", ...payload });
}

/** Fetch /api/agent/status into autonomyAtom. Fails safe to status:'error'. */
export async function refreshAutonomy() {
  // Hosted: /api/agent/status is a gated legacy single-operator route (404 by
  // design). Hosted worker runs are pinned local_only (remote_sandbox.py's
  // autonomy default; the control plane never overrides it), so resolve the
  // atom without a fetch.
  if (getAuthMode() === "hosted") {
    autonomyAtom.set({ status: "ok", autonomy: "local_only" });
    return;
  }
  try {
    const s = await api.agentStatus();
    applyAutonomyStatus(s);
  } catch {
    autonomyAtom.set({ status: "error" });
  }
}

/**
 * Map an /api/agent/status payload to the composer chip view-model.
 * FAIL SAFE: loading / error / missing-autonomy never read as autopilot.
 * @returns {{label:string, tone:'calm'|'armed'|'neutral', dot:string, tooltip:string}}
 */
export function describeAutonomy(s) {
  if (!s || s.status === "loading") {
    return {
      label: "Autonomy…",
      tone: "neutral",
      dot: "neutral",
      tooltip: "Checking agent autonomy…",
    };
  }
  if (s.status === "error" || !s.autonomy) {
    return {
      label: "Autonomy: unknown",
      tone: "neutral",
      dot: "neutral",
      tooltip:
        "Could not determine agent autonomy. Assume nothing reaches SAP CPQ without your explicit push.",
    };
  }
  if (s.autonomy === "autopilot") {
    const t = s.tenant_host || _hostFromUrl(s.tenant_url) || s.tenant || "the live tenant";
    const sani = s.sanitise_on_push
      ? " Scripts are sanitised on push."
      : " Scripts are pushed raw (sanitiser off).";
    return {
      label: `Autopilot → ${t}`,
      tone: "armed",
      dot: "armed",
      tooltip: `The agent pushes changes directly to the ${t} SAP CPQ cloud.${sani}`,
    };
  }
  // local_only — the default, safe mode.
  return {
    label: "Local-only",
    tone: "calm",
    dot: "calm",
    tooltip:
      "The agent works in your workspace and asks before any change reaches SAP CPQ. Destructive actions always ask.",
  };
}

function _hostFromUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    return new URL(raw).host;
  } catch {
    return raw
      .replace(/^https?:\/\//, "")
      .replace(/\/Login\.aspx\/?$/i, "")
      .replace(/\/$/, "");
  }
}

// ---------------------------------------------------------------------------
// Live run state — the dock's plan + tool timeline
// ---------------------------------------------------------------------------

/** A fresh, idle run. */
export function makeIdleRun() {
  return { status: "idle", startedAt: null, plan: [], steps: [], nowLabel: "", summary: null };
}

export const runAtom = signal(makeIdleRun());

// Per-conversation live parts (INCLUDING text/reasoning) for the inline
// assistant drawer. Distinct from runAtom (tool-only dock projection): the
// drawer needs the streamed prose. Keyed by conversation id; a fresh map
// object per set() so signal's Object.is equality actually fires.
export const assistantRunAtom = signal({});

export function publishAssistantParts(convId, parts, running) {
  assistantRunAtom.set({ ...assistantRunAtom.peek(), [convId]: { parts, running } });
}

export function resetAssistantRun(convId) {
  if (convId == null) { assistantRunAtom.set({}); return; }
  const next = { ...assistantRunAtom.peek() };
  delete next[convId];
  assistantRunAtom.set(next);
}

/**
 * Pure reducer for the live run. Handles run-meta events only — plan, steps,
 * and now-line are derived from parts via projectDock.
 * Retired cases (tool_call / tool_result / file_write / patch_step) are gone;
 * the dock is a stateless projection of parts now.
 */
export function applyRunEvent(run, ev) {
  switch (ev.type) {
    case "run_started":
      return { ...makeIdleRun(), status: "running", startedAt: ev.ts || null };
    case "status":
      return { ...run, status: "running", nowLabel: ev.label || run.nowLabel };
    case "plan":
      return { ...run, status: run.status === "idle" ? "running" : run.status, plan: ev.todos || [] };
    case "final":
      return { ...run, status: "final", nowLabel: "", summary: ev.summary != null ? ev.summary : run.summary };
    case "error":
      return { ...run, status: "error", nowLabel: "" };
    default:
      return run;
  }
}

// ---------------------------------------------------------------------------
// Dock projection — derive the dock view-model from the turn's parts.
// Replaces runAtom.steps[] (which was mutated by tool_call/tool_result/file_write).
// ---------------------------------------------------------------------------

/**
 * Project the dock view-model from a turn's parts array.
 * Returns { status, plan, steps, nowLabel } in the shape renderSteps consumes.
 *
 * @param {Array}  parts   - the live assistant message's parts[]
 * @param {object} opts
 * @param {boolean} [opts.running=true]   - whether the run is still in progress
 * @param {string}  [opts.nowLabel=""]    - the current status label (from agent_status)
 * @returns {{ status: string, plan: Array, steps: Array, nowLabel: string }}
 */
export function projectDock(parts, { running = true, nowLabel = "" } = {}) {
  const planPart = parts.find((p) => p.type === "plan");
  const plan = planPart ? (planPart.todos || []) : [];
  const steps = parts
    .filter((p) => p.type === "tool")
    .map((p) => ({
      id: p.id,
      kind: p.subtype === "file" ? "file_write" : undefined,
      verb: p.verb,
      target: p.target,
      why: p.why,
      risk: p.risk,
      status: p.status === "running" ? "running" : p.status,
      ms: p.durationMs,
      path: (p.args && p.args.path) || undefined,
    }));
  return { status: running ? "running" : "final", plan, steps, nowLabel };
}

/** Dispatch a run event into runAtom (called by the WS handler). */
export function dispatchRun(ev) {
  runAtom.set(applyRunEvent(runAtom.peek(), ev));
}

/** Reset the run to idle (e.g., when the user sends a fresh message). */
export function resetRun() {
  runAtom.set(makeIdleRun());
}

/**
 * Map a workspace path to {apiKind, name} for api.syncDiff. null if not syncable.
 * Exported so agent-parts.js can resolve a file-write path to a sync target.
 */
export function syncTargetFromPath(path) {
  if (!path) return null;
  const m = String(path).match(/^(global-scripts|custom-calculations|ui-templates)\/(.+)\.(py|html)$/);
  if (!m) return null;
  const apiKind = m[1] === "global-scripts" ? "scripts"
    : m[1] === "custom-calculations" ? "calculations" : "templates";
  return { apiKind, name: m[2] };
}

/**
 * Toggle a file-write step's inline diff.
 *
 * NOTE: Steps are now a stateless projection of parts (projectDock).
 * The patch_step reducer case has been removed, so this function cannot store
 * expanded/diff state on the step until a later change restores it by reading from the
 * part directly. For now this is intentionally a safe no-op — clicking a
 * file-write row in the dock does nothing rather than throwing.
 */
export async function toggleStepDiff(_id) {
  // Uses syncTargetFromPath; inline diff is now rendered by renderToolBodyRich
  // in agent-parts.js. The dock step row remains a no-op here.
}

// ---------------------------------------------------------------------------
// Tool legibility — render a raw tool call as a CPQ consultant's action
// ---------------------------------------------------------------------------

export const toolCatalogAtom = signal({}); // { shortName: {verb, noun, why, risk} }

/** Load /api/agent/tool-catalog once. Non-fatal: describeTool falls back. */
export async function loadToolCatalog() {
  try {
    const r = await api.agentToolCatalog();
    const map = {};
    for (const t of r.tools || []) map[t.name] = t;
    toolCatalogAtom.set(map);
  } catch {
    /* non-fatal */
  }
}

// A few common non-MCP tools the agent uses directly.
const _VERB_FALLBACK = {
  Read: "Read", Write: "Wrote", Edit: "Edited", MultiEdit: "Edited",
  Bash: "Ran", Grep: "Searched", Glob: "Found files", TodoWrite: "Updated plan",
};

function _humanizeVerb(short) {
  if (_VERB_FALLBACK[short]) return _VERB_FALLBACK[short];
  const s = String(short).replace(/[_-]+/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Ran";
}

function _extractTarget(args) {
  if (!args || typeof args !== "object") return "";
  const a = args;
  const v = a.path || a.name || a.file || a.query || a.command || a.attribute ||
            a.table || a.script || a.partNumber || a.product || "";
  if (v) return String(v);
  if (typeof a.code === "string") return a.code.slice(0, 40).replace(/\s+/g, " ");
  return "";
}

/**
 * Map a raw tool call to a legible action: human verb + target + plain-language
 * "why" + risk. Uses the tool catalog when available, else a safe humanized
 * fallback (risk defaults to 'read'). Pure.
 * @returns {{tool:string, verb:string, why:string, risk:string, target:string}}
 */
export function describeTool(toolName, args, catalog) {
  const short = String(toolName || "").replace(/^mcp__.+?__/, "");
  const entry = (catalog || {})[short] || null;
  return {
    tool: short,
    verb: (entry && entry.verb) || _humanizeVerb(short),
    why: (entry && entry.why) || "",
    // The catalog provides risk_level; accept risk as an alias for robustness.
    risk: (entry && (entry.risk_level || entry.risk)) || "read",
    target: _extractTarget(args),
  };
}
